//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { RegisterOptions, useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import Badge, { BadgeType } from '@components/atoms/badge/Badge';
import { BodyText } from '@components/atoms/text/Text';
import { FormFieldController } from '@components/form/field-controller/FormFieldController';
import { FormTextAreaInput } from '@components/form/text-area/FormTextAreaInput';
import { FormTextInput } from '@components/form/text-input/FormTextInput';
import { DEFAULT_EMPTY_VALUE } from '@constants/DefaultEmptyValues';
import { isTruthy } from '@utils/filterPredicates';

// Define the types for the keys
type RootFieldKeys = keyof EditableBVSamplingProduct;
type VariationKeys = keyof EditableBVSamplingProduct['variations'][number];

// Define the union type for form field names
type FormFieldName = RootFieldKeys | `variations.${number}.${VariationKeys}`;

interface FormFieldProps {
  helpText?: string;
  inputType?: 'text' | 'textarea';
  isDisabled?: boolean;
  isViewOnly?: boolean;
  label: string;
  labelHelpText?: string;
  name: FormFieldName;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  rules?: RegisterOptions | undefined;
  visibleToParticipants?: boolean;
  width?: 'half' | 'full';
}

export const FormField = ({
  helpText = '',
  inputType = 'text',
  isDisabled = false,
  isViewOnly = false,
  label,
  labelHelpText = '',
  name,
  onValueChange,
  placeholder = '',
  rules,
  visibleToParticipants = false,
  width,
}: FormFieldProps) => {
  const { control, getValues } = useFormContext();

  if (isViewOnly) {
    const value = getValues(name);

    return (
      <ViewOnlyContainer>
        <BodyText>{label}</BodyText>
        <BodyText>{isTruthy(value) ? value : DEFAULT_EMPTY_VALUE}</BodyText>
      </ViewOnlyContainer>
    );
  }
  return (
    <Container width={width}>
      {visibleToParticipants && (
        <StyledBadge
          size='small'
          text='Visible to Participants'
          type={BadgeType.Secondary}
        />
      )}
      <FormFieldController control={control} name={name} rules={rules}>
        {(props) => {
          const InputComponent =
            inputType === 'textarea' ? FormTextAreaInput : FormTextInput;
          return (
            <InputComponent
              {...props}
              helpText={helpText}
              isDisabled={isDisabled}
              label={label}
              labelHelpText={labelHelpText}
              onChange={(value: string) => {
                props.onChange(value);
                if (onValueChange) {
                  onValueChange(value);
                }
              }}
              placeholder={placeholder}
              trackingId={`${SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_INPUT_FIELD}-${name}`}
            />
          );
        }}
      </FormFieldController>
    </Container>
  );
};

const Container = styled.div<{ width?: 'half' | 'full' }>`
  position: relative;
  width: ${({ width = 'full' }) => (width === 'half' ? '50%' : '100%')};
`;

const StyledBadge = styled(Badge)`
  position: absolute;
  right: 0;
  top: 0;
`;

const ViewOnlyContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[1]};
`;
